import React from 'react';
import { compose, omit } from 'ramda';
import { dispatcher } from 'dispatcher';
import { withHandlers, withProps } from 'recompose';
import Form from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import { componentService } from 'js/services/component.service';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useShowNotification } from 'js/components/common/Notifications';
import { withPortlet } from 'js/components/system/Portlet/hocs/withPortlet';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { confirm, int, length, notEqual, required } from 'js/utils/Validation/validationRules';

import { changePin } from './actions';
import { FORM_FIELDS, PIN_CHANGE } from './constants';

import style from './index.scss';

export const pinValidationConfig = (key, addNotEqual) => {
    const rules = [
        required({ error: `playtech.mobile.changepin.validation.required.${key}` }),
        int({ re: /^\d+$/, error: 'playtech.mobile.changepin.validation.length' }),
        length({ min: 4, max: 4, error: 'playtech.mobile.changepin.validation.length' })
    ];

    if (addNotEqual) {
        rules.push(notEqual({
            field: FORM_FIELDS.CURRENT_PIN,
            error: 'playtech.mobile.changepin.validation.likePrevious'
        }));
    }

    return rules;
};

export const validationConfig = {
    [FORM_FIELDS.CURRENT_PIN]: pinValidationConfig('currentPin'),
    [FORM_FIELDS.NEW_PIN]: pinValidationConfig('newPin', true),
    [FORM_FIELDS.VERIFY_PIN]: [
        confirm({ field: FORM_FIELDS.NEW_PIN, error: 'playtech.mobile.changepin.validation.match' })
    ]
};

export const PinChangeBare = ({ formatData, onSubmit, t: translate }) => {
    const t = (tKey, ...args) => translate(`playtech.mobile.changepin.placeholder.${tKey}`, ...args);

    return (
        <Form
            onSubmit={onSubmit}
            formatData={formatData}
            validationConfig={validationConfig}
            render={({ submitting, pristine, submitError, handleSubmit }) => (
                <form
                    className={style.form}
                    onSubmit={event => event.preventDefault()}
                >
                    <div className={style.title}>{translate('playtech.mobile.changepin.title')}</div>
                    {submitError && <ErrorMessage error={submitError} animate={true} />}

                    <Input
                        type="password"
                        className={style.input}
                        id="changePIN-currentPIN"
                        name={FORM_FIELDS.CURRENT_PIN}
                        label={t(FORM_FIELDS.CURRENT_PIN)}
                        placeholder={t(FORM_FIELDS.CURRENT_PIN)}
                        aria-label={translate([
                            `playtech.mobile.changepin.placeholder.${FORM_FIELDS.CURRENT_PIN}`,
                            'playtech.mobile.system.input.aria-label'
                        ])}
                    />

                    <Input
                        type="password"
                        id="changePIN-newPIN"
                        className={style.input}
                        name={FORM_FIELDS.NEW_PIN}
                        label={t(FORM_FIELDS.NEW_PIN)}
                        placeholder={t(FORM_FIELDS.NEW_PIN)}
                        aria-label={translate([
                            `playtech.mobile.changepin.placeholder.${FORM_FIELDS.NEW_PIN}`,
                            'playtech.mobile.system.input.aria-label'
                        ])}
                    />

                    <Input
                        className={style.input}
                        type="password"
                        id="changePIN-verifyPIN"
                        name={FORM_FIELDS.VERIFY_PIN}
                        label={t(FORM_FIELDS.VERIFY_PIN)}
                        placeholder={t(FORM_FIELDS.VERIFY_PIN)}
                        aria-label={translate([
                            `playtech.mobile.changepin.placeholder.${FORM_FIELDS.CURRENT_PIN}`,
                            'playtech.mobile.system.input.aria-label'
                        ])}
                    />

                    <div className={style.formActions}>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            className={style.submit}
                            disabled={submitting || pristine}
                        >
                            {translate('playtech.mobile.changepin.button.submit')}
                        </Button>
                    </div>
                </form>
            )}
        />
    );
};

export const PinChange = compose(
    withPortlet({ forLoggedIn: true }),
    withProps(() => ({
        showNotification: useShowNotification({ iconType: 'info' })
    })),
    withTranslations,
    withHandlers({
        formatData: () => omit([FORM_FIELDS.VERIFY_PIN]),
        onSubmit: ({ showNotification }) => async (values) => {
            let message;

            try {
                await changePin(values);
                showNotification({
                    tKey: 'playtech.mobile.changepin.request.success'
                });

            } catch (error) {
                const { response: { data: { errorMessage, errorCode } } } = error;

                if (errorCode === 303) {
                    dispatcher.dispatch('app:message', {
                        ID: 'twoFactorAuthentication',
                        data: {
                            onCancel: () => {
                                publicEventsAPIService.trigger('2fa-popup-closed', 'change-pin');
                            },
                            onSuccess: async () => {
                                await changePin(values);
                                showNotification({
                                    tKey: 'playtech.mobile.changepin.request.success'
                                });
                            },
                            resendHandler: () => changePin(values)
                        }
                    });

                    return '';
                }

                message = errorMessage;

                showNotification({ tKey: errorMessage });
            }

            return message;
        }
    })
)(PinChangeBare);

componentService.registerPageComponents({ [PIN_CHANGE]: PinChange });
